import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faXmark, faQrcode, faUser } from '@fortawesome/free-solid-svg-icons';
import { createInstance, generateApiKey } from '../../../services/api';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../../config/api';
import './CreateInstanceModal.css';

const CreateInstanceModal = ({ 
  isOpen, 
  onClose, 
  currentUser, 
  enterprise, 
  onInstanceCreated, 
  onQRModal 
}) => {
  const [instanceName, setInstanceName] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userInstanceCount, setUserInstanceCount] = useState(0);
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [selectedOwnerInstanceCount, setSelectedOwnerInstanceCount] = useState(0);
  const { t } = useTranslation();

  // Buscar usuários da enterprise e dados iniciais
  useEffect(() => {
    if (isOpen && currentUser?.id) {
      fetchEnterpriseUsers();
      fetchUserInstanceCount();
      
      // Define proprietário padrão como o usuário atual
      setSelectedOwnerId(currentUser.id);
    }
  }, [isOpen, currentUser]);

  // Atualizar contador de instâncias quando o proprietário selecionado mudar
  useEffect(() => {
    if (selectedOwnerId) {
      fetchSelectedOwnerInstanceCount();
    }
  }, [selectedOwnerId]);

  const fetchEnterpriseUsers = async () => {
    try {
      const users = await apiRequest(`/enterprises/${enterprise.id}/users`);

      // Filtrar apenas usuários Normal para Admin selecionar, mas incluir o próprio Admin
      const availableUsers = users?.filter(user =>
        user.enterprise_role === 'Normal' || user.id === currentUser.id
      ) || [];

      setEnterpriseUsers(availableUsers);
    } catch (error) {
      console.error('Erro ao buscar usuários da enterprise:', error);
      setEnterpriseUsers([]);
    }
  };

  const fetchUserInstanceCount = async () => {
    try {
      const userInstances = await apiRequest(`/instances/user/${currentUser.id}`);

      setUserInstanceCount(userInstances?.length || 0);
    } catch (error) {
      console.error('Erro ao buscar instâncias do usuário:', error);
      setUserInstanceCount(0);
    }
  };

  const fetchSelectedOwnerInstanceCount = async () => {
    try {
      const ownerInstances = await apiRequest(`/instances/user/${selectedOwnerId}`);

      setSelectedOwnerInstanceCount(ownerInstances?.length || 0);
    } catch (error) {
      console.error('Erro ao buscar instâncias do proprietário selecionado:', error);
      setSelectedOwnerInstanceCount(0);
    }
  };

  const resetForm = () => {
    setInstanceName('');
    setSelectedOwnerId(currentUser?.id || '');
    setLoading(false);
    setErrorMessage('');
    setUserInstanceCount(0);
    setSelectedOwnerInstanceCount(0);
    setEnterpriseUsers([]);
  };

  const handleCreateInstance = async () => {
    if (!instanceName.trim()) {
      setErrorMessage(t('enterprise.createInstance.nameRequired'));
      return;
    }

    if (!selectedOwnerId) {
      setErrorMessage(t('enterprise.createInstance.ownerRequired'));
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      // Busca dados do proprietário selecionado
      const selectedOwner = enterpriseUsers.find(u => u.id === selectedOwnerId) || currentUser;
      
      // Verifica limite do proprietário selecionado APENAS se não for Admin
      if (selectedOwner.enterprise_role !== 'Admin') {
        const ownerLimit = selectedOwner.max_instances || 3;
        if (selectedOwnerInstanceCount >= ownerLimit) {
          throw new Error(t('enterprise.createInstance.limitReached', { 
            userName: selectedOwner.name, 
            limit: ownerLimit 
          }));
        }
      }

      // Verifica limite da enterprise
      const enterpriseUsers = await apiRequest(`/enterprises/${enterprise.id}/users`);
      const enterpriseUserIds = enterpriseUsers?.map(u => u.id) || [];

      let enterpriseInstanceCount = 0;
      for (const userId of enterpriseUserIds) {
        const userInstances = await apiRequest(`/instances/user/${userId}`);
        enterpriseInstanceCount += userInstances?.length || 0;
      }

      const enterpriseLimit = enterprise.max_instances || 10;

      if (enterpriseInstanceCount >= enterpriseLimit) {
        throw new Error(t('enterprise.createInstance.enterpriseLimitReached', { limit: enterpriseLimit }));
      }

      // Cria a instância usando a API
      const newInstance = await apiRequest('/sessions', {
        method: 'POST',
        body: JSON.stringify({
          instanceName: instanceName.trim(),
          name: instanceName.trim(),
          server_id: 1,
          user_id: selectedOwnerId
        }),
      });

      const instanceNameFromApi = newInstance.instanceName || instanceName.trim();

      // Fecha o modal atual
      resetForm();
      onClose();

      // Abre o modal do QR após um pequeno delay
      setTimeout(() => {
        // Chama a função para abrir o modal de QR com os dados da instância criada
        const instanceForQR = {
          id: newInstance.id,
          name: instanceName.trim(),
          instanceName: instanceNameFromApi,
          server_id: 1
        };

        // Se há uma função onQRModal, chama ela
        if (onQRModal) {
          onQRModal(instanceForQR);
        } else {
          // Senão, chama onInstanceCreated que deve abrir o QR
          onInstanceCreated(instanceForQR);
        }
      }, 500);

    } catch (error) {
      setErrorMessage(error.message || 'Erro ao criar instância');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedOwnerData = () => {
    return enterpriseUsers.find(u => u.id === selectedOwnerId) || currentUser;
  };

  if (!isOpen) return null;

  const selectedOwnerData = getSelectedOwnerData();

  return (
    <div className="create-instance-modal-overlay">
      <div className="create-instance-modal">
        <div className="modal-header">
          <div className="header-content">
            <FontAwesomeIcon icon={faTag} className="header-icon" />
            <h2 className="header-title">{t('enterprise.createInstance.title')}</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="input-container">
            <label htmlFor="instanceName">{t('enterprise.createInstance.name')}</label>
            <input
              id="instanceName"
              type="text"
              placeholder={t('enterprise.createInstance.namePlaceholder')}
              className="instance-name-input"
              value={instanceName}
              onChange={(e) => setInstanceName(e.target.value)}
              disabled={loading}
              maxLength={50}
            />
          </div>

          {/* Seleção de proprietário para Admin */}
          {currentUser?.enterprise_role === 'Admin' && (
            <div className="input-container">
              <label htmlFor="instanceOwner">
                <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5rem' }} />
                {t('enterprise.createInstance.owner')}
              </label>
              <select
                id="instanceOwner"
                className="owner-select"
                value={selectedOwnerId}
                onChange={(e) => setSelectedOwnerId(e.target.value)}
                disabled={loading}
              >
                <option value={currentUser.id}>{t('enterprise.createInstance.myInstances', { name: currentUser.name })}</option>
                {enterpriseUsers
                  .filter(user => user.id !== currentUser.id)
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {t('enterprise.createInstance.userWithEmail', { name: user.name, email: user.email })}
                    </option>
                  ))
                }
              </select>
            </div>
          )}

          {errorMessage && (
            <div className="error-message">
              {errorMessage}
            </div>
          )}

          <div className="instance-info">
            <div className="info-item">
              <span className="info-label">{t('enterprise.createInstance.owner')}:</span>
              <span className="info-value">
                {selectedOwnerData?.name || selectedOwnerData?.email}
                {selectedOwnerId === currentUser?.id && ` (${t('enterprise.createInstance.you')})`}
              </span>
            </div>
            
            {selectedOwnerData?.enterprise_role === 'Admin' ? (
              <div className="info-item">
                <span className="info-label">{t('enterprise.createInstance.limit')}:</span>
                <span className="info-value">
                  {t('enterprise.createInstance.noIndividualLimit')}
                </span>
              </div>
            ) : (
              <div className="info-item">
                <span className="info-label">{t('enterprise.createInstance.instanceLimit')}:</span>
                <span className="info-value">
                  {selectedOwnerInstanceCount} / {selectedOwnerData?.max_instances || 3}
                </span>
              </div>
            )}
          </div>

          <button
            className="create-button"
            onClick={handleCreateInstance}
            disabled={!instanceName.trim() || !selectedOwnerId || loading}
          >
            <FontAwesomeIcon icon={faQrcode} className="button-icon" />
            <span>{loading ? t('enterprise.createInstance.creating') : t('enterprise.createInstance.create')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateInstanceModal; 